/**
 * The composer's emoji suggestions after ":" and two characters (Discord):
 * the person's server emoji first, then the bundled Unicode set, names
 * starting with what was typed before names containing it. Choosing a
 * server emoji writes `:name:` (sent as `<:name:id>`); a Unicode one writes
 * the emoji itself.
 */
import { usableCustomEmoji } from '../store/emoji';
import { tokenOf } from './customEmoji';
import { EMOJI, emojiName } from './emoji';

export interface EmojiSuggestion {
  /** What `Emoji` draws: the character(s), or a custom emoji's token. */
  token: string;
  /** Shown as `:name:`. */
  name: string;
  /** What goes into the field. */
  insert: string;
  custom: boolean;
}

/** `:` and 2-32 name characters right before the caret (after a space or at the start). */
export const EMOJI_QUERY_RE = /(^|\s):([a-z0-9_+-]{2,32})$/i;

const UNICODE = Object.values(EMOJI).flat();

export function suggestEmoji(query: string, limit = 10): EmojiSuggestion[] {
  const q = query.toLowerCase();
  const rank = (name: string) => (name.startsWith(q) ? 0 : name.includes(q) ? 1 : 2);
  const custom = usableCustomEmoji()
    .filter((e) => rank(e.name) < 2)
    .sort((a, b) => rank(a.name) - rank(b.name))
    .map((e) => ({ token: tokenOf(e), name: e.name, insert: `:${e.name}: `, custom: true }));
  const uni = UNICODE.map((x) => ({ x, words: x.n.split(' ') }))
    .filter(({ words }) => words.some((w) => w.includes(q)))
    .map(({ x, words }) => ({
      x,
      r: words.some((w) => w.startsWith(q)) ? 0 : 1,
    }))
    .sort((a, b) => a.r - b.r)
    .map(({ x }) => ({
      token: x.e,
      name: emojiName(x.e) || x.n,
      insert: x.e + ' ',
      custom: false,
    }));
  return [...custom, ...uni].slice(0, limit);
}
